// @horsehub/shared — single source of truth for types, enums, DynamoDB key
// builders and cost/quota constants. Imported by both the web app and the API.
// ──────────────────────────────────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────────────────────────────────
export const QUOTA_BYTES = 500 * 1024 * 1024; // 500 MB hard cap per user
export const HORSE_SHARE_ROLES = ['co-owner', 'viewer'];
export const HORSE_SHARE_RANK = { 'co-owner': 2, viewer: 1 };
/** True when `role` is at least `min` in the share hierarchy. */
export function shareRoleAtLeast(role, min) {
    return HORSE_SHARE_RANK[role] >= HORSE_SHARE_RANK[min];
}
export const MAX_SHARES_PER_HORSE = 10;
export const MAX_HORSES = 3; // free/individual owners ("more coming soon" beyond this)
export const MAX_HORSES_FARM_OWNER = 100; // yard owners keep their own horses too
export const TRIAL_DAYS = 90; // 3-month free trial (now farm-scoped)
export const MAX_GALLERY = 12; // photos per horse gallery
export const MAX_NEWS_IMAGES = 5; // images per news post
export const FEED_FOLLOW_CAP = 50; // fan-out cap for the following feed
export const MAX_FARMS = 1; // one farm per farm-owner for now ("more coming soon")
export const FARM_PRICE_MONTHLY_GBP = 1.99; // flat MONTHLY price per yard (Stripe subscription)
export const FREE_DOCS_PER_HORSE = 5; // free tier: a few documents per horse
export const PAID_DOCS_PER_HORSE = 50; // paid horse-owner: higher document cap
export const MAX_AVATAR_BYTES = 5 * 1024 * 1024; // avatars are small; anything bigger is abuse
export const MAX_INVOICE_LINES = 20;
export const MAX_INVOICE_LINE_PENCE = 1_000_000; // £10,000 per line — sanity ceiling
export const MAX_PACKAGE_PRICE_PENCE = 500_000; // £5,000/month package ceiling
export const GENDERS = ['Mare', 'Stallion', 'Gelding'];
export const HORSE_STATUSES = ['active', 'sold', 'retired', 'deceased'];
export const MEDICAL_CATEGORIES = [
    'vaccination', 'deworming', 'dental', 'farrier', 'vet_exam',
    'bloodwork', 'injury', 'medication', 'allergy', 'weight_condition', 'other',
];
/** Categories that make sense as recurring reminders. */
export const REMINDER_CATEGORIES = [
    'vaccination', 'deworming', 'dental', 'farrier', 'vet_exam', 'other',
];
export const REMINDER_INTERVALS = [1, 2, 3, 4];
export const DEFAULT_VISIBILITY = {
    info: 'public',
    photos: 'public',
    news: 'public',
    pedigree: 'public',
    location: 'country',
};
export const DEFAULT_ROLES = { horseOwner: true, farmOwner: false };
export const FARM_SHARE_CATEGORIES = ['medical', 'documents', 'reminders', 'info'];
export const FARM_ROLE_RANK = { owner: 4, manager: 3, groom: 2, boarder: 1 };
/** Roles an invite can carry (owner is never assignable). */
export const ASSIGNABLE_FARM_ROLES = ['manager', 'groom', 'boarder'];
export const LIVERY_PACKAGES = ['full', 'part', 'diy', 'grass', 'schooling', 'retirement', 'other'];
export const INVOICE_STATUSES = ['draft', 'sent', 'paid', 'void'];
export const TICKET_STATUSES = ['open', 'resolved'];
export const TICKET_CATEGORIES = ['general', 'care', 'facilities', 'health', 'billing'];
export const TICKET_PRIORITIES = ['normal', 'urgent'];
// ──────────────────────────────────────────────────────────────────────────
// DynamoDB key builders (single-table design)
//   GSI1 = ownership / reverse lookups
//   GSI2 = public discovery (single PUBLIC partition, recency-sorted)
//   GSI3 = reminders due-date sweep
// ──────────────────────────────────────────────────────────────────────────
export const PKs = {
    user: (uid) => `USER#${uid}`,
    horse: (hid) => `HORSE#${hid}`,
    farm: (fid) => `FARM#${fid}`,
};
export const SKs = {
    profile: 'PROFILE',
    horseMeta: 'META',
    farmMeta: 'META',
    medical: (id) => `MED#${id}`,
    reminder: (id) => `REM#${id}`,
    document: (id) => `DOC#${id}`,
    news: (id) => `NEWS#${id}`,
    follow: (hid) => `FOLLOW#${hid}`,
    follower: (uid) => `FOLLOWER#${uid}`,
    invite: (token) => `INVITE#${token}`,
    member: (uid) => `MEMBER#${uid}`,
    boarding: (hid) => `BOARD#${hid}`,
    ticket: (tid) => `TICKET#${tid}`,
    /** Date-prefixed so a day's tasks are one cheap begins_with query. */
    task: (date, tid) => `TASK#${date}#${tid}`,
    invoice: (iid) => `INV#${iid}`,
    share: (uid) => `SHARE#${uid}`,
    shareInvite: (token) => `SHAREINV#${token}`,
};
export const SKPrefix = {
    horse: 'HORSE#', // also the GSI1SK prefix for a user's horses
    farm: 'FARM#', // GSI1SK prefix for a user's farm(s)
    medical: 'MED#',
    reminder: 'REM#',
    document: 'DOC#',
    news: 'NEWS#',
    follow: 'FOLLOW#',
    follower: 'FOLLOWER#',
    invite: 'INVITE#',
    member: 'MEMBER#',
    boarding: 'BOARD#',
    ticket: 'TICKET#',
    task: 'TASK#',
    taskOnDate: (date) => `TASK#${date}#`,
    invoice: 'INV#',
    share: 'SHARE#',
    shareInvite: 'SHAREINV#',
};
/**
 * GSI1 (ownership, overloaded): list a user's horses; a user's farm; a horse's
 * followers; and find invites by token. Same PK shape `USER#<uid>` is shared by
 * horses (SK `HORSE#…`) and farms (SK `FARM#…`) — disambiguated via begins_with.
 */
export const GSI1 = {
    ownerOfHorse: (uid) => `USER#${uid}`,
    horseSortByCreated: (createdAt, hid) => `HORSE#${createdAt}#${hid}`,
    horseFollowers: (hid) => `HORSE#${hid}`,
    followerSort: (uid) => `FOLLOWER#${uid}`,
    ownerOfFarm: (uid) => `USER#${uid}`,
    farmSortByCreated: (createdAt, fid) => `FARM#${createdAt}#${fid}`,
    inviteByToken: (token) => `INVITETOKEN#${token}`,
    ticketByUser: (uid) => `USER#${uid}`,
    ticketSortByCreated: (createdAt, tid) => `TICKET#${createdAt}#${tid}`,
    // Membership is a many-to-many access grant: list every yard a user has joined.
    memberByUser: (uid) => `USER#${uid}`,
    memberFarmSort: (fid) => `MEMBER#${fid}`,
    // A member's invoices across every yard they belong to.
    invoiceByMember: (uid) => `USER#${uid}`,
    invoiceSortByCreated: (createdAt, iid) => `INV#${createdAt}#${iid}`,
    // Horses shared WITH a user (the mirror of ownerOfHorse, which lists their own).
    shareByUser: (uid) => `USER#${uid}`,
    shareSortByCreated: (createdAt, hid) => `SHARE#${createdAt}#${hid}`,
    shareInviteByToken: (token) => `SHAREINVTOKEN#${token}`,
};
/**
 * GSI2 (overloaded): public horse discovery (partition `PUBLIC`) AND pending
 * invites by email (partition `INVITEEMAIL#<email>`), so a new signup can find
 * invites addressed to them.
 */
export const GSI2 = {
    publicPartition: 'PUBLIC',
    recencySort: (lastActiveAt, hid) => `${lastActiveAt}#${hid}`,
    inviteByEmail: (email) => `INVITEEMAIL#${email.toLowerCase()}`,
    inviteEmailSort: (createdAt, token) => `${createdAt}#${token}`,
    shareInviteByEmail: (email) => `SHAREINVEMAIL#${email.toLowerCase()}`,
};
/** GSI3 (reminders): one partition, sorted by due date (YYYY-MM-DD). */
export const GSI3 = {
    reminderPartition: 'REMINDER',
    dueSort: (dueDate, hid, rid) => `${dueDate}#${hid}#${rid}`,
};
// ──────────────────────────────────────────────────────────────────────────
// Small helpers
// ──────────────────────────────────────────────────────────────────────────
/** True when `role` meets or beats `min` in the yard hierarchy. */
export function roleAtLeast(role, min) {
    return FARM_ROLE_RANK[role] >= FARM_ROLE_RANK[min];
}
/** £-format an integer pence amount, e.g. 4550 → "£45.50". */
export function formatPence(pence) {
    return `£${(pence / 100).toFixed(2)}`;
}
export function usagePercent(usedBytes, quotaBytes = QUOTA_BYTES) {
    if (quotaBytes <= 0)
        return 0;
    return Math.min(100, Math.round((usedBytes / quotaBytes) * 100));
}
export function kindFromMime(mime) {
    if (mime === 'application/pdf')
        return 'pdf';
    if (mime.startsWith('image/'))
        return 'image';
    if (mime.includes('word') || mime.includes('officedocument'))
        return 'word';
    return 'other';
}
/** YYYY-MM-DD in UTC. */
export function dueDateKey(iso) {
    return new Date(iso).toISOString().slice(0, 10);
}
export function addMonthsIso(iso, months) {
    const d = new Date(iso);
    d.setUTCMonth(d.getUTCMonth() + months);
    return d.toISOString();
}
//# sourceMappingURL=index.js.map